import { effect, inject, Injectable, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from '../../domain/models/session/session';
import { SessionCtl } from './session.ctl.service';

export const PAGE_TITLE_AREAS = new InjectionToken<Readonly<Record<string, string>>>('PAGE_TITLE_AREAS', {
  providedIn: 'root', factory: () => ({}),
});

const PAGE_TITLE_STORAGE_PREFIX = 'klab.page-title:';

export function resolvePageTitle(
  url: string,
  menu: readonly MenuItem[],
  data: { title?: string; areaLabel?: string; permission?: string; area?: string },
  areas: Readonly<Record<string, string>>,
): string {
  const path = (value: string) => value.trim().split(/[?#]/)[0].replace(/\/+$/, '');
  const current = path(url);
  if (current === '/admin/home') return 'Inicio · Klab';
  if (current === '/login') return 'Iniciar sesión · Klab';
  if (current === '/recover-password') return 'Recuperar contraseña · Klab';
  const nodes: MenuItem[] = [];
  const visit = (items: readonly MenuItem[]) => items.forEach(item => {
    nodes.push(item);
    visit(item.children ?? []);
  });
  visit(menu);
  const portal = current === '/app' || current.startsWith('/app/') ? 'Colaborador' : 'Admin';
  const candidates = nodes.filter(item => {
    const route = path(item.route ?? '');
    return item.permission_type !== 'ACTION' && route.startsWith(portal === 'Colaborador' ? '/app/' : '/admin/');
  });
  const exact = candidates.find(item => path(item.route ?? '') === current);
  const permission = candidates.find(item => item.code?.trim().toUpperCase() === data.permission?.trim().toUpperCase());
  const parent = candidates.filter(item => current.startsWith(`${path(item.route ?? '')}/`))
    .sort((a, b) => path(b.route ?? '').length - path(a.route ?? '').length)[0];
  const name = exact?.name?.trim() || data.title?.trim() || data.areaLabel?.trim() || permission?.name?.trim() || parent?.name?.trim();
  const area = portal === 'Colaborador' ? portal : areas[data.area ?? ''] || portal;
  return [...new Set([name, area, 'Klab'].filter(Boolean))].join(' · ');
}

@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly session = inject(SessionCtl);
  private readonly areas = inject(PAGE_TITLE_AREAS);
  private readonly navigation = toSignal(this.router.events.pipe(filter(event => event instanceof NavigationEnd)));

  constructor() {
    effect(() => {
      this.navigation();
      const menu = this.session.menu;
      if (!this.router.navigated) return;
      let route = this.router.routerState.snapshot.root;
      let data = { ...route.data };
      while (true) {
        const child = route.children.find(candidate => candidate.outlet === PRIMARY_OUTLET);
        if (!child) break;
        route = child;
        data = { ...data, ...route.data };
        if (route.title) data['title'] = route.title;
      }
      const pageTitle = resolvePageTitle(this.router.url, menu, data, this.areas);
      this.title.setTitle(pageTitle);
      this.persistTitle(this.router.url, pageTitle);
    });
  }

  private persistTitle(url: string, pageTitle: string): void {
    try {
      sessionStorage.setItem(`${PAGE_TITLE_STORAGE_PREFIX}${normalizeTitlePath(url)}`, pageTitle);
    } catch {
      return;
    }
  }
}

function normalizeTitlePath(url: string): string {
  return url.trim().split(/[?#]/)[0].replace(/\/+$/, '') || '/';
}
